import React from 'react'
import { motion } from 'motion/react'

/**
 * Two-segment yes/no picker: `[brushed | skipped]`, the current answer
 * highlighted. Emits the stored value, 1 or 0 — never the label
 * (`docs/bool_features.md`). No current value (unknown) highlights neither.
 *
 * `disabledValues` greys out an answer that can't be picked, e.g. a bool map
 * key another pair already uses.
 */
interface BoolToggleProps {
    labels: [string, string]
    value: number | null | undefined
    onChange: (value: 1 | 0) => void
    disabledValues?: number[]
    /** Focus the current answer (or the first) on mount, so the keyboard
     *  lands inside the toggle when it opens in place of a chip. */
    autoFocus?: boolean
}

const BoolToggle: React.FC<BoolToggleProps> = ({ labels, value, onChange, disabledValues, autoFocus }) => {
    const segments: Array<{ v: 1 | 0; label: string }> = [
        { v: 1, label: labels[0] },
        { v: 0, label: labels[1] },
    ]
    const focusValue = value === 0 ? 0 : 1
    return (
        <div role="group" className="inline-flex items-center gap-1">
            {segments.map(({ v, label }) => {
                const selected = value != null && Number(value) === v
                const disabled = !selected && (disabledValues ?? []).includes(v)
                return (
                    <motion.button
                        key={v}
                        type="button"
                        aria-pressed={selected}
                        disabled={disabled}
                        autoFocus={autoFocus && v === focusValue}
                        onClick={(e) => {
                            e.stopPropagation()
                            onChange(v)
                        }}
                        whileHover={disabled ? undefined : { scale: 1.1 }}
                        whileTap={disabled ? undefined : { scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className={`px-2 py-0.5 rounded-full text-sm whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-action ${
                            selected
                                ? 'bg-action text-plum'
                                : disabled
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-amber-900 hover:bg-amber-200'
                        }`}
                    >
                        {label}
                    </motion.button>
                )
            })}
        </div>
    )
}

export default BoolToggle
